import { z } from "zod";

const RESERVED_PROFILE_NAMES = new Set(["hermes", "test", "tmp", "root", "sudo"]);

/** Hermes Profile 是隔离的 Agent 实例，不等同于 HerSkin 的头像/显示名设置。 */
export const HermesProfileNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Profile 名称不能为空")
  .max(64, "Profile 名称最多 64 个字符")
  .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/u, "Profile 名称只能包含小写字母、数字、下划线和短横线");

export const NewHermesProfileNameSchema = HermesProfileNameSchema.refine(
  (value) => value !== "default" && !RESERVED_PROFILE_NAMES.has(value),
  "该名称由 Hermes 保留，不能用于新 Profile",
);

export const HermesProfileSchema = z
  .object({
    // `default` 是 Hermes 自带 Profile，读取目录时必须允许；仅“新建”时拒绝。
    name: HermesProfileNameSchema,
    isDefault: z.boolean(),
    isActive: z.boolean(),
    isCurrentRuntime: z.boolean(),
    description: z.string().max(2_000),
    model: z.string().max(300).nullable(),
    provider: z.string().max(200).nullable(),
    skillCount: z.number().int().min(0),
    gatewayRunning: z.boolean(),
  })
  .strict();

export const HermesProfileCatalogSchema = z
  .object({
    items: z.array(HermesProfileSchema),
    activeProfile: HermesProfileNameSchema,
    currentRuntimeProfile: HermesProfileNameSchema,
  })
  .strict();

export const CreateHermesProfileRequestSchema = z
  .object({
    name: NewHermesProfileNameSchema,
    description: z.string().trim().max(2_000).default(""),
    /** null 创建空白 Profile；非空时只复制能力配置，不复制会话和 Memory。 */
    cloneFrom: HermesProfileNameSchema.nullable().default(null),
  })
  .strict();

export const UpdateHermesProfileDescriptionRequestSchema = z
  .object({ description: z.string().trim().max(2_000) })
  .strict();

export const HermesProfileMutationResponseSchema = z
  .object({
    updated: z.literal(true),
    profile: HermesProfileSchema,
    catalog: HermesProfileCatalogSchema,
  })
  .strict();

export type HermesProfileName = z.infer<typeof HermesProfileNameSchema>;
export type HermesProfile = z.infer<typeof HermesProfileSchema>;
export type HermesProfileCatalog = z.infer<typeof HermesProfileCatalogSchema>;
export type CreateHermesProfileRequest = z.infer<typeof CreateHermesProfileRequestSchema>;
export type UpdateHermesProfileDescriptionRequest = z.infer<
  typeof UpdateHermesProfileDescriptionRequestSchema
>;
export type HermesProfileMutationResponse = z.infer<typeof HermesProfileMutationResponseSchema>;
